// SuperAdminDemoRequestsPage.jsx
//
// Manages inbound leads from the marketing site's "Book a demo" form.
//
// Layout:
//   - Status filter chips at the top
//   - Table of requests (most recent first)
//   - Each row expands to show full details + a status dropdown
//
// Status lifecycle:
//   new → contacted → scheduled → demoed → closed_won / closed_lost
//   (or new → spam)

import React, { useEffect, useState } from 'react';
import {
  Mail, Phone, Building2, Briefcase, Clock, MessageSquare,
  RefreshCw, ChevronDown, ChevronRight, ExternalLink, AlertCircle,
  CheckCircle, Calendar, Trash2, Globe2, Sparkles,
} from 'lucide-react';
import { listDemoRequests, updateDemoRequestStatus } from '../../lib/db';
import { formatDate } from '../../lib/formatters';

// Visual metadata per status. Keys must match the CHECK constraint on
// public.demo_requests.status.
const STATUS_META = {
  new:          { label: 'New',         Icon: Sparkles,     tone: 'bg-orange-900/40 text-orange-300 border-orange-700/40' },
  contacted:    { label: 'Contacted',   Icon: Mail,         tone: 'bg-blue-900/40 text-blue-300 border-blue-700/40' },
  scheduled:    { label: 'Scheduled',   Icon: Calendar,     tone: 'bg-indigo-900/40 text-indigo-300 border-indigo-700/40' },
  demoed:       { label: 'Demoed',      Icon: CheckCircle,  tone: 'bg-purple-900/40 text-purple-300 border-purple-700/40' },
  closed_won:   { label: 'Closed (won)',  Icon: CheckCircle, tone: 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40' },
  closed_lost:  { label: 'Closed (lost)', Icon: AlertCircle, tone: 'bg-slate-800 text-slate-400 border-slate-700' },
  spam:         { label: 'Spam',        Icon: Trash2,       tone: 'bg-red-900/40 text-red-300 border-red-700/40' },
};

const STATUS_FILTERS = ['all', 'new', 'contacted', 'scheduled', 'demoed', 'closed_won', 'closed_lost', 'spam'];

// All possible status transitions — shown as a dropdown on each row.
const ALL_STATUSES = ['new', 'contacted', 'scheduled', 'demoed', 'closed_won', 'closed_lost', 'spam'];

export default function SuperAdminDemoRequestsPage() {
  const [rows,        setRows]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [filter,      setFilter]      = useState('all');
  const [expandedId,  setExpandedId]  = useState(null);
  const [updating,    setUpdating]    = useState({});
  const [flashMsg,    setFlashMsg]    = useState('');

  const refresh = () => {
    setLoading(true);
    setError('');
    listDemoRequests({ status: filter === 'all' ? null : filter, limit: 200 })
      .then(({ data, error: err }) => {
        if (err) setError(err.message || 'Could not load demo requests');
        else setRows(data || []);
        setLoading(false);
      });
  };

  // Re-fetch whenever the filter changes. Includes a deps lint exception
  // for refresh (it's stable; we don't want a render loop).
  useEffect(refresh, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = async (id, newStatus) => {
    setUpdating((u) => ({ ...u, [id]: true }));
    const { data, error: err } = await updateDemoRequestStatus(id, newStatus);
    setUpdating((u) => ({ ...u, [id]: false }));
    if (err) {
      setFlashMsg(`Could not update: ${err.message}`);
      return;
    }
    if (data) {
      // Optimistic update — replace the row in place.
      setRows((rs) => rs.map((r) => r.id === id ? data : r));
      setFlashMsg(`Updated ${data.full_name} to "${STATUS_META[newStatus]?.label || newStatus}".`);
    }
  };

  // Build counts by status for the filter chips. Note: filter='all' shows
  // the total of all loaded rows, which equals the current filter result.
  // If the current filter is 'new', then `counts.new === rows.length` and
  // the other status counts are 0 — that's correct, we only loaded "new".
  // The pragmatic fix would be to load all + filter client-side, but at
  // ~200-row scale this is fine.
  const counts = STATUS_FILTERS.reduce((acc, k) => {
    acc[k] = k === 'all' ? rows.length : rows.filter((r) => r.status === k).length;
    return acc;
  }, {});

  if (loading && rows.length === 0) {
    return <div className="text-center py-20 text-slate-500">Loading demo requests…</div>;
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-white">Demo requests</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            Inbound leads from the "Book a demo" form on the marketing site.
            New requests trigger an email to{' '}
            <span className="text-slate-300 font-mono text-xs">
              hello@docketflowapp.com
            </span>
            {' '}automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium transition disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Error / flash */}
      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-900/30 border border-red-800 text-red-300 text-sm flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {flashMsg && (
        <div className="px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 text-slate-200 text-sm">
          {flashMsg}
        </div>
      )}

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2 text-xs">
        {STATUS_FILTERS.map((s) => {
          const isAll = s === 'all';
          const meta  = STATUS_META[s];
          return (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`px-2.5 py-1 rounded-lg capitalize transition ${
                filter === s
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {isAll ? 'All' : (meta?.label || s)}
              {counts[s] !== undefined && (
                <span className="opacity-70 ml-1">({counts[s]})</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-800/40">
            <tr>
              <th className="text-left px-5 py-3 font-medium w-8" />
              <th className="text-left px-5 py-3 font-medium">When</th>
              <th className="text-left px-5 py-3 font-medium">Name</th>
              <th className="text-left px-5 py-3 font-medium">Firm</th>
              <th className="text-left px-5 py-3 font-medium">Status</th>
              <th className="text-right px-5 py-3 font-medium">Change status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center text-slate-500">
                  <Sparkles size={24} className="mx-auto text-slate-700 mb-2" />
                  No demo requests yet. When visitors submit the form on{' '}
                  <a
                    href="/#/book-demo"
                    className="text-indigo-400 hover:underline"
                  >
                    /#/book-demo
                  </a>
                  , they'll appear here.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.new;
              const Icon = meta.Icon;
              const isOpen = expandedId === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr
                    className={`hover:bg-slate-800/30 transition cursor-pointer ${isOpen ? 'bg-slate-800/20' : ''}`}
                    onClick={() => setExpandedId(isOpen ? null : r.id)}
                  >
                    <td className="px-5 py-3 text-slate-500">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-400 whitespace-nowrap">
                      {formatDate(r.created_at, { includeTime: true })}
                    </td>
                    <td className="px-5 py-3">
                      <p className="text-slate-200 text-sm font-medium">{r.full_name}</p>
                      <p className="text-xs text-slate-500">{r.email}</p>
                    </td>
                    <td className="px-5 py-3 text-slate-300 text-sm">
                      {r.firm_name}
                      {r.firm_size && (
                        <span className="text-xs text-slate-500 ml-2">· {r.firm_size}</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium border ${meta.tone}`}>
                        <Icon size={11} /> {meta.label}
                      </span>
                    </td>
                    <td
                      className="px-5 py-3 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <select
                        value={r.status}
                        disabled={updating[r.id]}
                        onChange={(e) => handleStatusChange(r.id, e.target.value)}
                        className="px-2.5 py-1 rounded-lg text-xs bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-40"
                      >
                        {ALL_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {STATUS_META[s]?.label || s}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>

                  {/* Expanded detail row */}
                  {isOpen && (
                    <tr className="bg-slate-900/60">
                      <td />
                      <td colSpan={5} className="px-5 py-5">
                        <DemoRequestDetail row={r} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footnote */}
      <p className="text-xs text-slate-500">
        Status changes are saved immediately. Need a different recipient email for new-request alerts?{' '}
        Update the <span className="font-mono text-slate-400">demo_notification_email</span>{' '}
        row in <span className="font-mono text-slate-400">public.platform_settings</span>.
      </p>
    </div>
  );
}

// ─── Detail row ────────────────────────────────────────────────────

function DemoRequestDetail({ row }) {
  const detailItem = (Icon, label, value) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-2">
        <Icon size={14} className="text-slate-500 mt-0.5 shrink-0" />
        <div>
          <p className="text-xs text-slate-500">{label}</p>
          <p className="text-sm text-slate-200">{value}</p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {detailItem(Mail,      'Email',          row.email)}
        {detailItem(Phone,     'Phone',          row.phone)}
        {detailItem(Building2, 'Firm size',      row.firm_size)}
        {detailItem(Briefcase, 'Role',           row.role)}
        {detailItem(Briefcase, 'Practice area',  row.practice_area)}
        {detailItem(Clock,     'Preferred time', row.preferred_time)}
      </div>

      {row.message && (
        <div className="rounded-xl bg-slate-900 border border-slate-800 p-4">
          <div className="flex items-start gap-2">
            <MessageSquare size={14} className="text-slate-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="text-xs text-slate-500 mb-1">Their message</p>
              <p className="text-sm text-slate-200 whitespace-pre-wrap">{row.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Context */}
      {(row.user_agent || row.referrer) && (
        <div className="text-xs text-slate-500 space-y-1 pt-3 border-t border-slate-800">
          {row.referrer && (
            <p>
              <Globe2 size={11} className="inline mr-1.5" />
              Referrer: <span className="text-slate-400">{row.referrer}</span>
            </p>
          )}
          {row.user_agent && (
            <p className="font-mono text-[10px] leading-relaxed">
              {row.user_agent}
            </p>
          )}
        </div>
      )}

      {/* Quick actions */}
      <div className="flex flex-wrap items-center gap-2 pt-2">
        <a
          href={`mailto:${row.email}?subject=${encodeURIComponent('Your DocketFlow demo request')}&body=${encodeURIComponent(`Hi ${row.full_name.split(' ')[0]},\n\nThanks for requesting a demo of DocketFlow. I'd love to walk you through how it can help ${row.firm_name}.\n\nWould any of these times work for you?\n  • \n  • \n  • \n\nLooking forward to talking.\n\nBest,\nThe DocketFlow team`)}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition"
        >
          <Mail size={12} /> Reply by email
          <ExternalLink size={10} />
        </a>
        {row.phone && (
          <a
            href={`tel:${row.phone}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium transition"
          >
            <Phone size={12} /> Call
          </a>
        )}
        <span className="ml-auto text-xs text-slate-600 font-mono">
          {row.id}
        </span>
      </div>
    </div>
  );
}
